import React, { useEffect, useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import {
  calculateRiskScore,
  formatDocument,
  formatToReal,
  getLetterScore,
  getScoreLegends,
  maskCEP,
} from "../../../utils/utils";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import NoOcurrence from "../../common/query/NoOcurrence";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AvisoCard from "../../common/query/AvisoCard";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import ScoreRating from "./ScoreRating";
import { Badge, Button, Modal, Table } from "flowbite-react";
import { HiOutlineEye, HiThumbDown, HiThumbUp } from "react-icons/hi";
import StreetView from "../../common/query/StreetView";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import moment from "moment";
import Socios from "../../common/query/Socios";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { TfiBarChart } from "react-icons/tfi";
import { LuBuilding2 } from "react-icons/lu";
import { BsBank } from "react-icons/bs";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
  GrMoney,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { BiLineChartDown } from "react-icons/bi";

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const CadastralRestritivoScoreQueryPF = ({ data, dataFull }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [openMap, setOpenMap] = useState(false);
  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao
  );

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +data?.score?.score,
        +data?.score?.probabilidade
      )
    );
  }, [data]);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade && data?.dadosCadastrais?.uf
      ? `${data?.dadosCadastrais?.cidade} - ${data?.dadosCadastrais?.uf}`
      : data?.dadosCadastrais?.cidade || data?.dadosCadastrais?.uf,
    maskCEP(data?.dadosCadastrais?.cep),
  ]
    .filter(Boolean)
    .join(", ");

  const birthMoment = data?.dadosCadastrais?.dataNascimento
    ? moment(data?.dadosCadastrais?.dataNascimento, [
        "DD/MM/YYYY",
        "YYYY-MM-DD",
      ])
    : null;
  const birthLabel =
    birthMoment && birthMoment.isValid()
      ? birthMoment.format("DD/MM/YYYY")
      : null;
  const birthYears =
    birthMoment && birthMoment.isValid()
      ? moment().diff(birthMoment, "years")
      : null;
  const birthAgeLabel =
    birthYears != null
      ? `${birthYears} ano${birthYears === 1 ? "" : "s"}`
      : null;

  return (
    <div>
      <TitleStripe>
        <div className="flex items-center gap-2">
          <FiUserCheck className="text-blue-700" />
          <span>Dados Cadastrais / Base IV</span>
        </div>
      </TitleStripe>

      <div className="mt-4 text-xs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Nome
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {data?.dadosCadastrais?.nome || "Indisponível"}
            </p>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-gray-500">
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
            Data Nasc: {birthLabel || "Não informado"}
          </span>
          {birthAgeLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
              {birthAgeLabel}
            </span>
          )}
        </div>

        <div className="mt-3 grid gap-2 grid-cols-2">
          {[
            {
              label: "CPF",
              value: formatDocument(data?.dadosCadastrais?.cpf),
              hint: "Documento",
            },
            {
              label: "Nome da mãe",
              value: data?.dadosCadastrais?.nomeMae || "Não informado",
              hint: "Parente",
            },
            {
              label: "Sexo",
              value: data?.dadosCadastrais?.sexo || "Não informado",
              hint: "Gênero",
            },
            {
              label: "Telefone",
              value: data?.dadosCadastrais?.telefone || "Não informado",
              hint: "Contato",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-100 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>

        <TitleStripe className="mt-6 mb-3">
          <div className="flex items-center gap-2">
            <LuBuilding2 className="text-blue-700" /> <span>Localização</span>
          </div>
        </TitleStripe>

        <div className="grid gap-2 grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Endereço
            </p>
            <div className="flex items-center gap-2">
              <p className="text-[12px] font-semibold text-gray-900">
                {data?.dadosCadastrais?.endereco || "Não informado"}
              </p>
              {fullAddress && (
                <div className=" flex items-center gap-1 text-[11px] text-blue-600 cursor-pointer">
                  <span>- Ver fotos do local</span>
                  <HiOutlineEye className="text-base" />
                </div>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Bairro · CEP
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.bairro || "Não informado"}</span>
              <span>·</span>
              <span>{data?.dadosCadastrais?.cep || "--"}</span>
            </p>
          </div>
        </div>

        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Cidade
            </p>
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.cidade || "—"}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              UF
            </p>
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.uf || "--"}
            </p>
          </div>
        </div>
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <LuBuilding2 className="text-blue-700" />
            <span>Participação em empresas / Base IV</span>
          </div>
        </TitleStripe>
        {data?.participacaoEmEmpresas?.length > 0 ? (
          <ParticipacaoEmEmpresas empresas={data?.participacaoEmEmpresas} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div className="mb-4">
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <FaRegChartBar className="text-blue-700" />
            <span>Avaliação preliminar de crédito / Base IV</span>
          </div>
        </TitleStripe>
      </div>

      {data?.score?.score == 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <p className="font-medium">
            Não foi possível calcular o score desta empresa.
          </p>
          <p className="mt-1">
            É necessário verificar as outras informações disponíveis no
            relatório para uma análise completa.
          </p>
        </div>
      ) : (
        <EvaluationScorePJ
          data={data}
          score={data?.score?.score}
          probability={+data?.score?.probabilidade * 100}
          type="CNPJ"
          hasIncome={false}
        />
      )}

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <BsBank className="text-blue-700" />
            <span>Pendências Bancárias / Base IV</span>
          </div>
        </TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <>
            <PendenciasFinanceiras
              quantidadeTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
              }
              valorTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal
              }
              pendenciasFinanceiras={data?.pendenciasFinanceiras}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentExcel className="text-blue-700" />
            <span>Títulos protestados / Base IV</span>
          </div>
        </TitleStripe>
        {data?.protestos?.length > 0 ? (
          <Protestos
            quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
            protestos={data?.protestos}
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo / Base IV</span>
          </div>
        </TitleStripe>
        {data?.chequesSemFundo?.length > 0 ? (
          <>
            <ChequesSemFundos
              quantidadeTotal={
                data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal
              }
              chequesSemFundos={data?.chequesSemFundo}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      {data?.chequesSustados && (
        <div>
          <TitleStripe className="mb-5">
            <div className="flex items-center gap-2">
              <CiMoneyCheck1 className="text-blue-700" />
              <span>Cheques sustados / Base IV</span>
            </div>
          </TitleStripe>
          {data?.chequesSustados?.length > 0 ? (
            <>
              <ChequesSustados
                quantidadeTotal={
                  data?.resumoConsulta?.chequesSustados?.quantidadeTotal
                }
                chequesSustados={data?.chequesSustados}
              />
            </>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.acoesCiveis && (
        <div>
          <TitleStripe className="mb-5">
            <div className="flex items-center gap-2">
              <MdOutlineBalance className="text-blue-700" />
              <span>Ações Cíveis / Base IV</span>
            </div>
          </TitleStripe>
          {data?.acoesCiveis?.length > 0 ? (
            <>
              <AcoesCiveis
                quantidadeTotal={
                  data?.resumoConsulta?.acoesCiveis?.quantidadeTotal
                }
                valorTotal={data?.resumoConsulta?.acoesCiveis?.valorTotal}
                acoesCiveis={data?.acoesCiveis}
              />
            </>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas / Base IV</span>
          </div>
        </TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex-1">
              <HistoricoConsultas
                quantidadeTotal={data?.historicoConsultas?.length}
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          {fullAddress ? (
            <StreetView address={fullAddress} height={460} />
          ) : (
            <div className="p-3 text-sm text-gray-500">
              Endereço indisponível.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CadastralRestritivoScoreQueryPF;
